import { ReactNode, createContext, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useInvoices from 'hooks/useInvoices';
import useInvoiceAutoRefresh from 'hooks/useInvoiceAutoRefresh';
import useDeclarations from 'hooks/useDeclarations';
import { useRequestPromise } from 'hooks/useRequest';
import { Invoice } from 'store/invoices/types';
import { Declaration } from 'store/declarations/declaration';
import { getCustomer } from 'store/customers/client';
import { Customer } from 'store/customers/customer';
import { hasBillingDetails } from 'views/customers/utils';

type UseInvoicesReturn = ReturnType<typeof useInvoices>;

type InvoiceContextValue = {
    invoiceId?: string;
    invoice?: Invoice;
    isLoading: boolean;
    error: UseInvoicesReturn['error'];
    updateInvoice: UseInvoicesReturn['updateInvoice'];
    sendInvoiceToCustomer: UseInvoicesReturn['sendInvoiceToCustomer'];
    declaration?: Declaration;
    /** The party the invoice is billed to. The invoice itself only holds a snapshot of it. */
    customer: Customer | null;
    refetchCustomer: () => void;
    missingBillingDetails: boolean;
};

const InvoiceContext = createContext<InvoiceContextValue | null>(null);

type Props = {
    children: ReactNode;
};

const InvoiceContextProvider = ({ children }: Props) => {
    const { invoiceId } = useParams();
    const { invoice, isLoading, error, updateInvoice, sendInvoiceToCustomer } = useInvoices(invoiceId);
    const { declaration, getDeclaration } = useDeclarations();

    const customerId = invoice?.customer?.id;

    const { data: customer, refetch: refetchCustomer } = useRequestPromise(
        () => (customerId ? getCustomer(customerId) : null),
        undefined,
        [customerId]
    );

    // Only once the party is loaded - otherwise the warning and the disabled actions flash on first paint
    const missingBillingDetails = !!customer && !hasBillingDetails(customer);

    useInvoiceAutoRefresh(
        invoiceId,
        invoice?.status,
        invoice?.sendToCustomerEmailStatus?.overallStatus,
        invoice?.sendToCustomerEmailStatus?.sendInProgress
    );

    useEffect(() => {
        if (!invoice?.declarationId) {
            console.error('No declaration found for this invoice');
        }

        if (invoice?.declarationId) getDeclaration(invoice.declarationId);
    }, [invoice?.declarationId, getDeclaration]);

    return (
        <InvoiceContext.Provider
            value={{
                invoiceId,
                invoice,
                isLoading,
                error,
                updateInvoice,
                sendInvoiceToCustomer,
                declaration,
                customer,
                refetchCustomer,
                missingBillingDetails,
            }}
        >
            {children}
        </InvoiceContext.Provider>
    );
};

export const useInvoiceContext = () => {
    const context = useContext(InvoiceContext);

    if (!context) {
        throw new Error('useInvoiceContext must be used within an InvoiceContextProvider');
    }

    return context;
};

export default InvoiceContextProvider;
